import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import type { CardView } from "@breeze/shared";
import {
  copyVisit,
  createAccountContact,
  createAccountLocation,
  patchAccountFlags,
} from "../api.js";
import { pcaFind, pcaRetrieve } from "../pca.js";
import type { PcaFindItem } from "../pca.js";
import { STAGE_LABELS } from "@breeze/shared";
import { STAGE_ACCENT } from "../theme.js";
import { useState as useReactState } from "react";
import { useMessages } from "../hooks/useMessages.js";
import { useCardPresence } from "../hooks/usePresence.js";
import { useMarkRead, useMarkUnread } from "../hooks/useReadState.js";
import { useContactAnnotations } from "../hooks/useContactAnnotations.js";
import { useCardCluster } from "../hooks/useCluster.js";
import { QuoteEditor } from "./QuoteEditor.js";
import { NotifyModal } from "./NotifyModal.js";
import { NewQuoteModal } from "./NewQuoteModal.js";
import { CreateAccountModal } from "./CreateAccountModal.js";
import { LinkExistingCustomerModal } from "./LinkExistingCustomerModal.js";
import { Timeline } from "./Timeline.js";
import { Composer } from "./Composer.js";
import { CardActions } from "./CardActions.js";
import { PhoneLink } from "./PhoneLink.js";
import { NotesTab } from "./NotesTab.js";
import { ActivityTab } from "./ActivityTab.js";
import { BalanceSection } from "./AccountSlideout.js";
// PendingActionBanner removed 2026-04-24 — AI drafts now hydrate the
// composer directly (one editor, one scrollbar). See Composer.tsx.

type Tab = "Timeline" | "Account" | "Visits" | "Balance" | "Notes" | "Activity";
const CUSTOMER_TABS: Tab[] = ["Timeline", "Account", "Visits", "Balance", "Notes", "Activity"];
// Tech cards only need the conversation thread — the other tabs surface
// customer-pipeline data (B_TACCOUNT, B_TVISIT, customer notes) that
// doesn't exist for a technician.
const TECH_TABS: Tab[] = ["Timeline"];

// Best-effort split of the card's displayed name into first/last components
// so the Create-Account modal opens with something pre-filled. Falls back to
// empty strings — the modal requires at least firstName before it submits.
function firstNameFromCard(card: CardView): string {
  const raw = (card.contactName ?? card.accountName ?? "").trim();
  if (!raw) return "";
  const first = raw.split(/\s+/)[0] ?? "";
  return first;
}
function lastNameFromCard(card: CardView): string {
  const raw = (card.contactName ?? card.accountName ?? "").trim();
  if (!raw) return "";
  const parts = raw.split(/\s+/);
  if (parts.length <= 1) return "";
  return parts.slice(1).join(" ");
}

// Combine a visit's SCHEDULEDATE (YYYY-MM-DD) + STARTTIME (HH:MM or HH:MM:SS)
// into a single JS Date. Returns null if no date — time alone is meaningless
// without a date to anchor it.
function parseScheduledAt(date: string | null, time: string): Date | null {
  if (!date) return null;
  const t = (time || "").trim();
  const hhmm = t.length >= 5 ? t.slice(0, 5) : "09:00";
  const d = new Date(`${date}T${hhmm}:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Render the B_TVISIT WORKORDERSTATUS as a short, meaningful badge.
// "Booking not made (Qualified)" -> "QUOTE" (it's a quote, not a booking).
// Everything else strips the trailing "(Qualified)" / "(Converted)" suffix
// so the badge text reads cleanly.
function shortStatusLabel(raw: string): string {
  const r = raw.toLowerCase();
  if (r.includes("booking not made")) return "Quote";
  return raw.replace(/\s*\([^)]+\)\s*$/, "").trim();
}

// Colour variant for the status badge. Booking-made is ALWAYS green (a
// confirmed booking is a win regardless of whether it's in the past or
// future). Everything else is status-driven.
type StatusVariant = "blue" | "red" | "green" | "orange" | "neutral";
function statusVariant(raw: string, _scheduled: Date | null): StatusVariant {
  const r = raw.toLowerCase();
  if (!r) return "neutral";
  if (r.includes("cancel")) return "red";
  if (r.includes("booking made")) return "green";
  if (r.includes("qualified")) return "blue";
  if (r.includes("reviewing") && r.includes("converted")) return "green";
  if (r.includes("technician required")) return "blue";
  return "neutral";
}

// "Tue 28 Apr 2026, 13:30" — used for the scheduled appointment slot.
function fmtDayDateTime(d: Date): string {
  const date = d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${date}, ${time}`;
}

// "Wed 22 Apr 2026 10:48" — used for the greyed Created/Updated line.
function fmtShortDateTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const date = d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${date} ${time}`;
}

function VisitStatusBadge({
  raw,
  scheduled,
}: {
  raw: string;
  scheduled: Date | null;
}): JSX.Element | null {
  if (!raw) return null;
  const variant = statusVariant(raw, scheduled);
  const cls: Record<StatusVariant, string> = {
    blue: "border-slack-cyan/60 bg-slack-cyan/10 text-slack-cyan",
    red: "border-slack-pink/60 bg-slack-pink/10 text-slack-pink",
    green: "border-slack-green/60 bg-slack-green/10 text-slack-green",
    orange: "border-stage-chase/60 bg-stage-chase/10 text-stage-chase",
    neutral: "border-ink-500 bg-ink-800 text-ink-50",
  };
  return (
    <span
      className={`rounded-sm border px-1.5 py-0.5 text-[12px] uppercase tracking-wider ${cls[variant]}`}
      title={raw}
    >
      {shortStatusLabel(raw)}
    </span>
  );
}

type Props = {
  card: CardView | null;
  onClose: () => void;
};

export function Slideout({ card, onClose }: Props): JSX.Element {
  const [tab, setTab] = useState<Tab>("Timeline");
  const [quoteVid, setQuoteVid] = useReactState<number | null>(null);
  const [newQuoteOpen, setNewQuoteOpen] = useState(false);
  const [createAccountOpen, setCreateAccountOpen] = useState(false);
  const [linkExistingOpen, setLinkExistingOpen] = useState(false);
  const msgs = useMessages(card?.cardId ?? null);
  const markRead = useMarkRead();
  const markUnread = useMarkUnread();

  // Heartbeat presence while this card is open.
  useCardPresence(card?.cardId ?? null);

  // Pre-populate the Create-New-Account modal from the card's own timeline
  // so staff don't retype what we already know: latest inbound email's
  // from_address for the email field, latest inbound SMS/WhatsApp/VoIP's
  // from_address for the phone field. Falls back to empty strings when
  // the card has no matching message.
  const prefill = useMemo(() => {
    const messages = msgs.data?.messages ?? [];
    const inbound = messages.filter((m) => m.direction === "in" && m.fromAddress);
    const email =
      inbound.find((m) => m.channel === "email")?.fromAddress ?? "";
    const phone =
      inbound.find(
        (m) => m.channel === "sms" || m.channel === "whatsapp" || m.channel === "voip"
      )?.fromAddress ?? "";
    return { email, phone };
  }, [msgs.data]);

  // On card change just reset the tab. Read-state is NOT auto-cleared on
  // open any more — the user explicitly clicks the Done button in the
  // header to stop the flashing (handled by markDoneToggle below).
  useEffect(() => {
    if (!card) return;
    setTab("Timeline");
  }, [card?.cardId]);

  // Mirror Card.tsx's needsAttention calc so the Done button colour
  // matches the card's flash state. Card-side logic is the source of
  // truth; keep this in sync if that ever changes.
  const needsAttention = useMemo((): boolean => {
    if (!card) return false;
    const lastReadMs = card.lastReadAt ? new Date(card.lastReadAt).getTime() : 0;
    const hasUnreadInbound =
      card.lastMessageAt !== null &&
      card.lastMessageDirection === "in" &&
      new Date(card.lastMessageAt).getTime() > lastReadMs;
    const pendingAt = card.pendingAction?.createdAt ?? null;
    const hasUnreadPending =
      pendingAt !== null && new Date(pendingAt).getTime() > lastReadMs;
    const isManuallyFlagged = card.flaggedAt !== null;
    return hasUnreadInbound || hasUnreadPending || isManuallyFlagged;
  }, [
    card?.lastReadAt,
    card?.lastMessageAt,
    card?.lastMessageDirection,
    card?.pendingAction?.createdAt,
    card?.flaggedAt,
  ]);

  // Done button: yellow border while flashing → click marks read AND
  // clears flagged_at (server-side markCardRead does both). Once not
  // flashing, border turns green and the same button becomes the
  // restore-attention toggle: click marks unread → flagged_at = NOW(),
  // flash returns. This replaces the old auto-on-open + Mark Unread
  // pair with a single explicit hand-off control.
  const handleDoneToggle = (): void => {
    if (!card) return;
    if (needsAttention) markRead.mutate(card.cardId);
    else markUnread.mutate(card.cardId);
  };

  useEffect(() => {
    if (!card) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [card, onClose]);

  const accent = card ? STAGE_ACCENT[card.stage] : "#7E3A7F";
  const messages = msgs.data?.messages ?? [];

  return (
    <AnimatePresence>
      {card ? (
        <>
          <motion.div
            key="scrim"
            className="fixed inset-0 z-30 bg-ink-950/30"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            key="panel"
            className="fixed right-0 top-0 z-40 flex h-full w-full max-w-[100vw] flex-col border-l border-ink-700 bg-ink-800 shadow-drag md:w-[42rem]"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 280, damping: 32 }}
          >
            <div
              className="h-[3px] w-full"
              style={{ backgroundColor: accent }}
              aria-hidden
            />
            <header className="flex items-start justify-between gap-3 border-b border-ink-700 px-5 py-4">
              <div className="min-w-0 flex-1">
                <div
                  className="text-[12px] font-semibold uppercase tracking-[0.18em]"
                  style={{ color: accent }}
                >
                  {STAGE_LABELS[card.stage]}
                </div>
                <h2 className="mt-1 truncate text-2xl leading-tight text-ink-50">
                  {card.accountName ?? card.contactName ?? `Card #${card.cardId}`}
                </h2>
                <div className="mt-1 flex items-center gap-2 text-[13px] text-ink-50">
                  {card.postcode ? (
                    <span className="font-mono font-semibold uppercase tracking-wider">
                      {card.postcode}
                    </span>
                  ) : null}
                  {card.scheduleDate ? (
                    <>
                      <span className="text-ink-300">·</span>
                      <span className="tabular-nums">{card.scheduleDate}</span>
                    </>
                  ) : null}
                  {card.totalCost !== null && card.totalCost > 0 ? (
                    <>
                      <span className="text-ink-300">·</span>
                      <span className="tabular-nums font-semibold">
                        £{card.totalCost.toFixed(2)}
                      </span>
                    </>
                  ) : null}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {/* Quote / account affordances only apply to customer cards.
                    Tech cards (recruitment prospects + hired techs) are not
                    customers — they don't get a B_TACCOUNT / B_TCONTACT row. */}
                {card.stage === "tech" ? null : card.aid > 0 ? (
                  <button
                    onClick={() => setNewQuoteOpen(true)}
                    className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1.5 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight"
                    title="Create a new quote on this account — pick or add a contact + service location"
                  >
                    New Quote
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => setLinkExistingOpen(true)}
                      className="rounded-md border border-ink-500 px-3 py-1.5 text-[13px] font-medium text-ink-50 hover:border-ink-400 hover:bg-ink-700"
                      title="This is an existing customer — search by postcode / surname / email and link this card to their account"
                    >
                      Link existing customer
                    </button>
                    <button
                      onClick={() => setCreateAccountOpen(true)}
                      className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1.5 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight"
                      title="No Breeze account for this contact yet — create one and start a quote"
                    >
                      Create New Account
                    </button>
                  </>
                )}
                <button
                  onClick={handleDoneToggle}
                  disabled={markRead.isPending || markUnread.isPending}
                  className={`rounded-md border-2 px-3 py-1.5 text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                    needsAttention
                      ? "border-slack-yellow bg-slack-yellow/10 text-ink-50 hover:bg-slack-yellow/20"
                      : "border-slack-green bg-slack-green/10 text-ink-50 hover:bg-slack-green/20"
                  }`}
                >
                  Done
                </button>
                <button
                  onClick={onClose}
                  className="rounded-md border border-ink-500 px-3 py-1.5 text-[13px] text-ink-50 hover:border-ink-400 hover:bg-ink-700"
                  aria-label="Close"
                >
                  Close
                </button>
              </div>
            </header>

            <QuoteEditor vid={quoteVid} onClose={() => setQuoteVid(null)} />

            {card.aid > 0 ? (
              <NewQuoteModal
                open={newQuoteOpen}
                onClose={() => setNewQuoteOpen(false)}
                aid={card.aid}
                cardId={card.cardId}
                accountLabel={
                  card.accountName ?? card.contactName ?? `Account #${card.aid}`
                }
                onCreated={(newVid) => {
                  // Hand off to the editor; the cluster will have refreshed
                  // via the modal's invalidation + SSE card.updated.
                  setQuoteVid(newVid);
                }}
              />
            ) : (
              <>
                <LinkExistingCustomerModal
                  open={linkExistingOpen}
                  onClose={() => setLinkExistingOpen(false)}
                  cardId={card.cardId}
                  // Card re-points + data populates via the card.updated
                  // broadcast and the modal's cluster invalidation; nothing
                  // more to do here — staff stays on the card.
                  onLinked={() => {}}
                  onCreateNew={() => setCreateAccountOpen(true)}
                />
                <CreateAccountModal
                  open={createAccountOpen}
                  onClose={() => setCreateAccountOpen(false)}
                  cardId={card.cardId}
                  defaultFirstName={firstNameFromCard(card)}
                  defaultLastName={lastNameFromCard(card)}
                  defaultEmail={prefill.email}
                  defaultPhone={prefill.phone}
                  onCreated={(newVid) => {
                    setQuoteVid(newVid);
                  }}
                />
              </>
            )}

            <div className="border-b border-ink-700 px-5 py-2">
              <CardActions card={card} />
            </div>

            <nav className="scrollbar-none flex items-center gap-5 overflow-x-auto border-b border-ink-700 px-5 text-[13px]">
              {(card.stage === "tech" ? TECH_TABS : CUSTOMER_TABS).map((t) => {
                const active = tab === t;
                return (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={`-mb-px shrink-0 border-b-2 py-3 uppercase tracking-[0.14em] text-ink-50 transition-colors ${
                      active ? "border-slack-aubergineLight font-semibold" : "border-transparent"
                    }`}
                  >
                    {t}
                    {t === "Timeline" && messages.length > 0 ? (
                      <span className="ml-1.5 tabular-nums">
                        {messages.length}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </nav>

            <div className="scrollbar-slim flex-1 overflow-y-auto px-5 py-4">
              {tab === "Timeline" ? (
                <Timeline messages={messages} loading={msgs.isLoading} />
              ) : tab === "Account" ? (
                <AccountTab card={card} />
              ) : tab === "Visits" ? (
                <VisitsTab card={card} onOpenQuote={(v) => setQuoteVid(v)} />
              ) : tab === "Balance" ? (
                card.aid > 0 ? (
                  <BalanceSection aid={card.aid} />
                ) : (
                  <div className="text-[13px] text-ink-200">
                    No Breeze account linked to this card yet — balance will
                    appear once the customer is on file.
                  </div>
                )
              ) : tab === "Notes" ? (
                <NotesTab card={card} />
              ) : tab === "Activity" ? (
                <ActivityTab card={card} />
              ) : null}
            </div>

            {tab === "Timeline" ? (
              <Composer card={card} messages={messages} />
            ) : (
              <footer className="border-t border-ink-700 bg-ink-900/60 px-5 py-3 text-[12px] text-ink-300">
                Card #{card.cardId} · created{" "}
                {new Date(card.createdAt).toLocaleDateString()}
              </footer>
            )}
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}

function AccountTab({ card }: { card: CardView }): JSX.Element {
  const ann = useContactAnnotations(card.cid);
  const cluster = useCardCluster(card.cardId);
  const aiFields = new Set((ann.data?.annotations ?? []).map((a) => a.fieldName));
  const isAi = (field: string): boolean => aiFields.has(field);
  const Star = (): JSX.Element => (
    <span
      className="ml-1 inline-block text-[13px] align-middle"
      title="Populated by AI extractor"
    >
      ✨
    </span>
  );

  const data = cluster.data?.cluster;
  const accounts = data?.accounts ?? [];
  const contacts = data?.contacts ?? [];
  const addresses = data?.addresses ?? [];
  const multiAccount = accounts.length > 1;

  const primaryAid = accounts[0]?.aid ?? 0;

  return (
    <section className="space-y-6">
      {/* Accounts ------------------------------------------------------- */}
      <div>
        <div className="mb-2 text-[13px] font-semibold uppercase tracking-[0.14em] text-ink-100">
          Accounts
          {accounts.length > 0 ? (
            <span className="ml-1.5 text-ink-200">· {accounts.length}</span>
          ) : null}
        </div>
        {cluster.isLoading ? (
          <div className="text-[13px] text-ink-200">loading cluster…</div>
        ) : accounts.length === 0 ? (
          <div className="rounded-md border border-dashed border-ink-500 bg-ink-900/40 px-3 py-3 text-[13px] text-ink-200">
            No Breeze account found for this contact.
          </div>
        ) : (
          <ul className="divide-y divide-ink-700 rounded-md border border-ink-700 bg-ink-900/40">
            {accounts.map((a) => (
              <li key={a.aid} className="px-3 py-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[15px] font-semibold text-ink-50">
                      {a.accountName || "—"}
                    </div>
                    <div className="mt-0.5 font-mono text-[11px] text-ink-300">
                      AID {a.aid}
                      {a.accountNumber ? ` · ${a.accountNumber}` : ""}
                    </div>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <FlagToggle
                    aid={a.aid}
                    cardId={card.cardId}
                    label="PITA"
                    field="pita"
                    value={a.pita}
                    activeColor="slack-pink"
                  />
                  <FlagToggle
                    aid={a.aid}
                    cardId={card.cardId}
                    label="DNC"
                    field="dnc"
                    value={a.dnc}
                    activeColor="slack-yellow"
                  />
                  <FlagToggle
                    aid={a.aid}
                    cardId={card.cardId}
                    label="Marketing"
                    field="dncMarketing"
                    value={a.dncMarketing}
                    activeColor="slack-aubergineLight"
                    inverted
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Contacts ------------------------------------------------------- */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-ink-100">
            Contacts
            {contacts.length > 0 ? (
              <span className="ml-1.5 text-ink-200">· {contacts.length}</span>
            ) : null}
          </div>
          {primaryAid > 0 ? (
            <AddContactButton aid={primaryAid} cardId={card.cardId} />
          ) : null}
        </div>
        {contacts.length === 0 ? (
          <div className="text-[13px] text-ink-200">—</div>
        ) : (
          <ul className="divide-y divide-ink-700 rounded-md border border-ink-700 bg-ink-900/40">
            {contacts.map((c) => {
              const phone =
                c.phoneMobile || c.phoneMain || c.phoneHome || c.phoneWork || c.phoneOther;
              return (
                <li key={`${c.aid}-${c.cid}`} className="px-3 py-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[15px] font-semibold text-ink-50">
                        {c.fullName ||
                          `${c.firstName} ${c.lastName}`.trim() ||
                          "—"}
                        {c.cid === card.cid && (isAi("NAME_FIRST") || isAi("NAME_LAST"))
                          ? <Star />
                          : null}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-ink-100">
                        {c.email ? <span className="truncate">{c.email}</span> : null}
                        {phone ? (
                          <PhoneLink
                            number={phone}
                            className="font-mono text-ink-200"
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="font-mono text-[11px] text-ink-300">CID {c.cid}</div>
                      {multiAccount ? (
                        <div className="mt-0.5 text-[11px] text-ink-300">
                          {c.accountName || `AID ${c.aid}`}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Service addresses --------------------------------------------- */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-ink-100">
            Service addresses
            {addresses.length > 0 ? (
              <span className="ml-1.5 text-ink-200">· {addresses.length}</span>
            ) : null}
          </div>
          {primaryAid > 0 ? (
            <AddAddressButton aid={primaryAid} cardId={card.cardId} />
          ) : null}
        </div>
        {addresses.length === 0 ? (
          <div className="text-[13px] text-ink-200">—</div>
        ) : (
          <ul className="divide-y divide-ink-700 rounded-md border border-ink-700 bg-ink-900/40">
            {addresses.map((a) => (
              <li key={`${a.aid}-${a.lid}`} className="px-3 py-2.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[15px] font-semibold text-ink-50">
                      {[a.address1, a.address2].filter(Boolean).join(", ") || "—"}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-ink-100">
                      {a.city ? <span>{a.city}</span> : null}
                      {a.postcode ? (
                        <span className="font-mono uppercase text-ink-200">{a.postcode}</span>
                      ) : null}
                      {a.propertyType ? (
                        <span className="text-ink-200">{a.propertyType}</span>
                      ) : null}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-mono text-[11px] text-ink-300">LID {a.lid}</div>
                    {multiAccount ? (
                      <div className="mt-0.5 text-[11px] text-ink-300">
                        {a.accountName || `AID ${a.aid}`}
                      </div>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {card.closedReason ? (
        <div className="rounded-md border border-slack-pink/50 bg-slack-pink/10 px-3 py-2 text-[13px]">
          <div className="text-[12px] font-semibold uppercase tracking-wider text-slack-pink">
            Closed reason
          </div>
          <div className="mt-0.5 text-ink-50">{card.closedReason}</div>
        </div>
      ) : null}
    </section>
  );
}

function VisitsTab({
  card,
  onOpenQuote,
}: {
  card: CardView;
  onOpenQuote: (vid: number) => void;
}): JSX.Element {
  const cluster = useCardCluster(card.cardId);
  const qc = useQueryClient();
  const [copyingVid, setCopyingVid] = useState<number | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [notifyVid, setNotifyVid] = useState<number | null>(null);
  const notifyVisit =
    notifyVid !== null
      ? cluster.data?.cluster?.visits?.find((x) => x.vid === notifyVid) ?? null
      : null;

  const visits = cluster.data?.cluster?.visits ?? [];
  const multiAccount = (cluster.data?.cluster?.accounts?.length ?? 0) > 1;

  async function onCopy(vid: number): Promise<void> {
    setCopyingVid(vid);
    setCopyError(null);
    try {
      const res = await copyVisit(vid);
      // Refresh the cluster so the new visit appears, then open it in the editor.
      await qc.invalidateQueries({ queryKey: ["cluster", card.cardId] });
      await qc.invalidateQueries({ queryKey: ["board"] });
      onOpenQuote(res.newVid);
    } catch (err) {
      setCopyError(err instanceof Error ? err.message : "copy failed");
    } finally {
      setCopyingVid(null);
    }
  }

  // Sum unpaid balance across every visit in the cluster. Only confirmed
  // bookings count (we don't chase unbooked quotes via "outstanding").
  const outstandingTotal = useMemo(
    () =>
      visits
        .filter((v) =>
          (v.workOrderStatus ?? "")
            .toLowerCase()
            .includes("booking made")
        )
        .reduce((sum, v) => sum + (v.outstanding > 0 ? v.outstanding : 0), 0),
    [visits]
  );

  if (cluster.isLoading) {
    return <div className="text-[13px] text-ink-200">loading visits…</div>;
  }
  if (visits.length === 0) {
    return (
      <section className="rounded-md border border-dashed border-ink-600 bg-ink-900/40 px-4 py-8 text-center text-[13px] text-ink-300">
        No visits found for this customer cluster yet.
      </section>
    );
  }

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-ink-50">
          Visits <span className="text-ink-200">({visits.length})</span>
        </div>
        {outstandingTotal > 0.009 ? (
          <span
            className="rounded-sm border border-slack-yellow/50 bg-slack-yellow/10 px-1.5 py-0.5 text-[12px] font-semibold uppercase tracking-wider text-slack-yellow"
            title="Total unpaid balance across confirmed bookings"
          >
            Outstanding payment £{outstandingTotal.toFixed(2)}
          </span>
        ) : null}
      </div>
      {copyError ? (
        <div className="mb-2 rounded-md border border-slack-pink/50 bg-slack-pink/10 px-2 py-1 text-[13px] text-slack-pink">
          ⚠ {copyError}
        </div>
      ) : null}
      <ul className="space-y-2">
        {visits.map((v) => {
          const scheduled = parseScheduledAt(v.scheduleDate, v.startTime);
          const isFuture = scheduled !== null && scheduled.getTime() > Date.now();
          const statusLower = (v.workOrderStatus ?? "").toLowerCase();
          const isBookingMade =
            statusLower.includes("booking made") && statusLower.includes("converted");
          const isCancelled = statusLower.includes("cancel");
          // Scheduled date is only meaningful once a booking has been made
          // (or subsequently cancelled — staff still want to see when the
          // cancelled slot had been for). On un-booked quotes the date is
          // the CURDATE() placeholder from createBlankVisit.
          const showScheduled = scheduled !== null && (isBookingMade || isCancelled);
          // Tech only shown when the booking is actually confirmed
          // ("Booking made (Converted)"). "Technician required (Converted)"
          // has no tech assigned yet; "Reviewing (Converted)" is pre-assign.
          const showTech = isBookingMade && !!v.technicianName;
          // Payment state on confirmed bookings:
          //   - future booking with outstanding → yellow "unpaid"
          //   - past booking fully paid → green "paid"
          //   - past booking partially/un-paid: no tag (chasing past-due
          //     unpaid is a separate workflow, don't conflate).
          const showUnpaid = isBookingMade && isFuture && v.outstanding > 0.009;
          const showPaid = isBookingMade && !showUnpaid && v.paidAmount > 0;
          return (
            <li
              key={v.vid}
              className="rounded-md border border-ink-700 bg-ink-900/40 px-3 py-2.5 text-[13px]"
            >
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-semibold text-ink-50">
                      #{v.visitNumberText || v.vid}
                    </div>
                    <VisitStatusBadge raw={v.workOrderStatus} scheduled={scheduled} />
                    {v.postcode ? (
                      <span className="font-semibold uppercase text-ink-50">
                        {v.postcode}
                      </span>
                    ) : null}
                    {multiAccount ? (
                      <span className="text-[12px] text-ink-200">{v.accountName}</span>
                    ) : null}
                  </div>

                  {showScheduled ? (
                    <div className="mt-1 text-[13px] text-ink-50">
                      📅 {fmtDayDateTime(scheduled)}
                    </div>
                  ) : null}

                  {showTech ? (
                    <div className="mt-0.5 text-[13px] text-ink-50">
                      Tech: {v.technicianName}
                    </div>
                  ) : null}

                  {v.dateCreated || v.dateLastEdited ? (
                    <div className="mt-1 text-[11px] text-ink-300">
                      {v.dateCreated ? (
                        <>Created: {fmtShortDateTime(v.dateCreated)}</>
                      ) : null}
                      {v.dateCreated && v.dateLastEdited ? " · " : null}
                      {v.dateLastEdited ? (
                        <>Updated: {fmtShortDateTime(v.dateLastEdited)}</>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-semibold tabular-nums text-ink-50">
                    £{v.totalCost.toFixed(2)}
                  </div>
                  {showUnpaid ? (
                    <div className="text-[12px] font-semibold text-slack-yellow">
                      unpaid
                    </div>
                  ) : showPaid ? (
                    <div className="text-[12px] font-semibold text-slack-green">
                      paid
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={() => onOpenQuote(v.vid)}
                  className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-2.5 py-1 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight"
                >
                  Open
                </button>
                <button
                  onClick={() => onCopy(v.vid)}
                  disabled={copyingVid === v.vid}
                  className="rounded-md border border-ink-500 bg-ink-800 px-2.5 py-1 text-[13px] text-ink-50 hover:border-ink-400 hover:bg-ink-700 disabled:opacity-40"
                >
                  {copyingVid === v.vid ? "Copying…" : "Copy"}
                </button>
                <button
                  onClick={() => setNotifyVid(v.vid)}
                  className="rounded-md border border-ink-500 bg-ink-800 px-2.5 py-1 text-[13px] text-ink-50 hover:border-ink-400 hover:bg-ink-700"
                  title="Send a templated email / SMS / WhatsApp via the kanban pipeline"
                >
                  Send Notification
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {notifyVisit ? (
        <NotifyModal
          open={notifyVid !== null}
          onClose={() => setNotifyVid(null)}
          vid={notifyVisit.vid}
          cardId={card.cardId}
          orderLabel={`Order ${notifyVisit.visitNumberText || notifyVisit.vid}`}
          customerName={card.contactName ?? card.accountName ?? "Customer"}
        />
      ) : null}
    </section>
  );
}

// ── PITA / DNC / Marketing toggle (in AccountTab) ───────────────────────
// Optimistic toggle: flips the local cluster cache immediately, fires the
// PATCH request, rolls back on failure. The server broadcasts card.updated
// so other connected browsers update their badges in real time.
//
// `inverted=true` is used for the Marketing toggle: the underlying column
// is DNC_marketing (true = do-not-contact for marketing), but the UI shows
// "Marketing on" — so the visible state is the inverse of the stored flag.
// Static class lookup — Tailwind's JIT can't see dynamic color tokens, so
// every variant we want must be spelled out in source.
const FLAG_TOGGLE_ON_CLS: Record<string, string> = {
  "slack-pink":
    "border-slack-pink/60 bg-slack-pink/15 text-slack-pink hover:bg-slack-pink/25",
  "slack-yellow":
    "border-slack-yellow/60 bg-slack-yellow/15 text-slack-yellow hover:bg-slack-yellow/25",
  "slack-aubergineLight":
    "border-slack-aubergineLight/60 bg-slack-aubergineLight/15 text-slack-aubergineLight hover:bg-slack-aubergineLight/25",
};

function FlagToggle({
  aid,
  cardId,
  label,
  field,
  value,
  activeColor,
  inverted,
}: {
  aid: number;
  cardId: number;
  label: string;
  field: "pita" | "dnc" | "dncMarketing";
  value: boolean;
  activeColor: keyof typeof FLAG_TOGGLE_ON_CLS;
  inverted?: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const [pending, setPending] = useReactState(false);
  const visualOn = inverted ? !value : value;
  const onClick = async (): Promise<void> => {
    if (pending) return;
    setPending(true);
    try {
      await patchAccountFlags(aid, { [field]: !value });
      await qc.invalidateQueries({ queryKey: ["cluster", cardId] });
    } catch {
      // Server state is the source of truth — refetch unconditionally so
      // the UI re-syncs even if the PATCH failed.
      await qc.invalidateQueries({ queryKey: ["cluster", cardId] });
    } finally {
      setPending(false);
    }
  };
  const onCls = FLAG_TOGGLE_ON_CLS[activeColor] ?? "";
  const cls = visualOn
    ? onCls
    : "border-ink-500 bg-ink-800 text-ink-200 hover:border-ink-400";
  return (
    <button
      onClick={() => void onClick()}
      disabled={pending}
      className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider transition-colors disabled:opacity-50 ${cls}`}
      title={
        field === "dncMarketing"
          ? visualOn
            ? "Marketing emails/SMS allowed — click to opt out"
            : "Customer is opted out of marketing — click to opt back in"
          : visualOn
            ? `${label} flag set — click to clear`
            : `Click to set ${label} flag`
      }
    >
      <span
        className="mr-1 inline-block h-1.5 w-1.5 rounded-full align-middle"
        style={{ backgroundColor: visualOn ? "currentColor" : "#6b6878" }}
      />
      {label}
    </button>
  );
}

// ── + New Contact button (in AccountTab) ────────────────────────────────
// Opens a small modal with firstName/lastName/email/phone, POSTs to
// /api/accounts/:aid/contacts, invalidates the cluster query so the new
// contact shows up immediately. The server also broadcasts card.updated
// so every connected browser refreshes.
function AddContactButton({
  aid,
  cardId,
}: {
  aid: number;
  cardId: number;
}): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canSubmit =
    (first.trim() || last.trim() || email.trim() || phone.trim()) && !busy;

  function close(): void {
    setOpen(false);
    setFirst(""); setLast(""); setEmail(""); setPhone("");
    setErr(null); setBusy(false);
  }

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    setBusy(true); setErr(null);
    try {
      await createAccountContact(aid, {
        firstName: first.trim() || "*",
        lastName: last.trim(),
        email: email.trim(),
        phone: phone.trim(),
      });
      await qc.invalidateQueries({ queryKey: ["cluster", cardId] });
      close();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "create failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md border border-ink-500 bg-ink-800 px-2 py-0.5 text-[11px] font-medium text-ink-50 hover:border-ink-400"
      >
        + New contact
      </button>
      <AnimatePresence>
        {open ? (
          <>
            <motion.div
              key="s" className="fixed inset-0 z-[85] bg-ink-950/70"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={close}
            />
            <motion.div
              key="m" className="fixed inset-0 z-[85] flex items-center justify-center p-4"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            >
              <div
                className="w-full max-w-md rounded-xl border border-ink-700 bg-ink-800 shadow-drag"
                onClick={(e) => e.stopPropagation()}
              >
                <header className="flex items-center justify-between border-b border-ink-700 px-4 py-3">
                  <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                    Add contact
                  </div>
                  <button onClick={close} className="rounded-md border border-ink-500 px-2 py-0.5 text-[12px] text-ink-50 hover:border-ink-400">
                    Close
                  </button>
                </header>
                <div className="grid grid-cols-2 gap-2 px-4 py-4 text-[13px]">
                  <LabeledMiniInput label="First name" value={first} onChange={setFirst} autoFocus />
                  <LabeledMiniInput label="Last name" value={last} onChange={setLast} />
                  <LabeledMiniInput label="Email" value={email} onChange={setEmail} />
                  <LabeledMiniInput label="Phone" value={phone} onChange={setPhone} />
                </div>
                <footer className="flex items-center justify-between border-t border-ink-700 px-4 py-3 text-[12px]">
                  <span className={err ? "text-slack-pink" : "text-ink-200"}>
                    {err ?? "Blank first name saves as *"}
                  </span>
                  <button
                    onClick={() => void submit()}
                    disabled={!canSubmit}
                    className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {busy ? "Saving…" : "Save"}
                  </button>
                </footer>
              </div>
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    </>
  );
}

// ── + New Address button (in AccountTab) ────────────────────────────────
// Same pattern but with PCA lookup (postcode → address suggestions →
// pre-fill the four address fields).
function AddAddressButton({
  aid,
  cardId,
}: {
  aid: number;
  cardId: number;
}): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [a1, setA1] = useState("");
  const [a2, setA2] = useState("");
  const [a3, setA3] = useState("");
  const [city, setCity] = useState("");
  const [postcode, setPostcode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // PCA state
  const [pcaResults, setPcaResults] = useState<PcaFindItem[]>([]);
  const [pcaState, setPcaState] = useState<"idle" | "loading">("idle");

  function close(): void {
    setOpen(false);
    setA1(""); setA2(""); setA3(""); setCity(""); setPostcode("");
    setPcaResults([]); setPcaState("idle"); setErr(null); setBusy(false);
  }

  async function runFind(): Promise<void> {
    const term = postcode.trim() || [a1, city].filter(Boolean).join(" ").trim();
    if (!term) { setErr("Enter a postcode or address first"); return; }
    setPcaState("loading"); setErr(null);
    try {
      setPcaResults(await pcaFind(term));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "lookup failed");
    } finally {
      setPcaState("idle");
    }
  }

  async function pickPca(id: string): Promise<void> {
    try {
      const a = await pcaRetrieve(id);
      if (!a) { setErr("Retrieve failed"); return; }
      setA1(a.Line1); setA2(a.Line2); setA3(a.Line3);
      setCity(a.City); setPostcode(a.PostalCode.toUpperCase());
      setPcaResults([]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "retrieve failed");
    }
  }

  const canSubmit = (a1.trim() || postcode.trim()) && !busy;

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    setBusy(true); setErr(null);
    try {
      await createAccountLocation(aid, {
        address1: a1.trim(),
        address2: a2.trim(),
        address3: a3.trim(),
        city: city.trim(),
        postcode: postcode.trim().toUpperCase(),
      });
      await qc.invalidateQueries({ queryKey: ["cluster", cardId] });
      close();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "create failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md border border-ink-500 bg-ink-800 px-2 py-0.5 text-[11px] font-medium text-ink-50 hover:border-ink-400"
      >
        + New address
      </button>
      <AnimatePresence>
        {open ? (
          <>
            <motion.div
              key="s" className="fixed inset-0 z-[85] bg-ink-950/70"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={close}
            />
            <motion.div
              key="m" className="fixed inset-0 z-[85] flex items-center justify-center p-4"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            >
              <div
                className="w-full max-w-lg rounded-xl border border-ink-700 bg-ink-800 shadow-drag"
                onClick={(e) => e.stopPropagation()}
              >
                <header className="flex items-center justify-between border-b border-ink-700 px-4 py-3">
                  <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                    Add address
                  </div>
                  <button onClick={close} className="rounded-md border border-ink-500 px-2 py-0.5 text-[12px] text-ink-50 hover:border-ink-400">
                    Close
                  </button>
                </header>
                <div className="space-y-2 px-4 py-4 text-[13px]">
                  <div className="flex items-end gap-2">
                    <LabeledMiniInput label="Postcode lookup" value={postcode} onChange={setPostcode} autoFocus />
                    <button
                      type="button"
                      onClick={() => void runFind()}
                      disabled={pcaState === "loading"}
                      className="h-8 shrink-0 rounded-md border border-slack-aubergineLight bg-slack-aubergine px-2.5 text-[12px] font-medium text-ink-50 hover:bg-slack-aubergineLight disabled:opacity-40"
                    >
                      {pcaState === "loading" ? "…" : "Find"}
                    </button>
                  </div>
                  {pcaResults.length > 0 ? (
                    <ul className="max-h-48 overflow-y-auto divide-y divide-ink-700 rounded-md border border-ink-700 bg-ink-900/40 text-[12px]">
                      {pcaResults.map((r) => (
                        <li key={r.Id}>
                          <button
                            type="button"
                            onClick={() => void pickPca(r.Id)}
                            className="block w-full px-3 py-1.5 text-left text-ink-50 hover:bg-ink-700"
                          >
                            {r.Text}{r.Description ? ` · ${r.Description}` : ""}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <LabeledMiniInput label="Address 1" value={a1} onChange={setA1} />
                    <LabeledMiniInput label="Address 2" value={a2} onChange={setA2} />
                    <LabeledMiniInput label="Address 3" value={a3} onChange={setA3} />
                    <LabeledMiniInput label="City" value={city} onChange={setCity} />
                  </div>
                </div>
                <footer className="flex items-center justify-between border-t border-ink-700 px-4 py-3 text-[12px]">
                  <span className={err ? "text-slack-pink" : "text-ink-200"}>
                    {err ?? "Postcode or Address 1 required"}
                  </span>
                  <button
                    onClick={() => void submit()}
                    disabled={!canSubmit}
                    className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {busy ? "Saving…" : "Save"}
                  </button>
                </footer>
              </div>
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    </>
  );
}

function LabeledMiniInput({
  label,
  value,
  onChange,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-0.5 block text-[11px] uppercase tracking-[0.14em] text-ink-200">
        {label}
      </span>
      <input
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-full rounded-md border border-ink-500 bg-ink-900 px-2 text-[13px] text-ink-50 focus:border-slack-aubergineLight focus:outline-none"
      />
    </label>
  );
}
